import { useMemo, Fragment, useCallback } from "react";

import { Circle, Flex, IconButton } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { ICheck, TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { useEmployeeStore } from "app/stores/useEmployeeStore";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { CheckScanner } from "flows/Inventory/shared/components/CheckScanner";
import { ChecksList } from "flows/Inventory/shared/components/ChecksList";
import { ConnectionErrorModal } from "flows/Inventory/shared/components/ConnectionErrorModal";
import { TaskIsTakenModal } from "flows/Inventory/shared/components/TaskIsTakenModal";
import { useChecksList } from "flows/Inventory/shared/hooks/useChecksList";
import {
  shelfOptions,
  useFilteredChecks,
  useGroupedByPriorityTagChecks,
} from "flows/Inventory/shared/hooks/useFilteredChecks";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { SwipeablePageWrapper } from "shared/components/SwipeablePageWrapper";
import { IntlMessageId } from "shared/lang";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { AscDscIcon } from "ui/AscDscIcon/AscDscIcon";
import { DropDown } from "ui/DropDown/DropDown";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleM, TitleXS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useShelfStore } from "../../../shared/stores/useShelfStore";
import { ROLLING_INVENTORY_METADATA_REASONS } from "../constants";

export function RollingInventoryChecksList() {
  const intl = useIntl();
  const navigate = useNavigate();

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);
  const isAutoScanActive = useEmployeeStore((state) => state.autoScan);

  const {
    rawChecks,
    refetchChecks,
    isLoadingChecks,
    isLoadingFetchNextCheckAndStartCheck,
    startCheck,
    isErrorModalOpen,
    isTaskIsTakenModalOpen,
    onCloseErrorModal,
    onCloseTaskIsTakenModal,
  } = useChecksList({
    type: [TaskType.stock_check],
    metadataReason: ROLLING_INVENTORY_METADATA_REASONS,
    origin: PageName.INVENTORY_ROLLING_INVENTORY_CHECK_LIST_PAGE,
  });

  const { selectedShelf, shelfSortOrder, setSelectedShelf, toggleShelfSortOrder, reset } =
    useShelfStore(
      useShallow((state) => ({
        selectedShelf: state.selectedShelf,
        shelfSortOrder: state.shelfSortOrder,
        setSelectedShelf: state.setSelectedShelf,
        toggleShelfSortOrder: state.toggleShelfSortOrder,
        reset: state.reset,
      })),
    );

  const onSelectCheck = useCallback(
    (shelfId: string, priority: number) => {
      if (isNotNullNorUndefined(timedActivityInstance)) {
        openOngoingActivityModal();
        return;
      }
      startCheck(shelfId, priority);
    },
    [startCheck, timedActivityInstance],
  );

  const sortedAndFilteredChecks = useFilteredChecks(
    rawChecks as ICheck[],
    selectedShelf,
    shelfSortOrder,
    TaskType.stock_check,
  );

  const sortedFilteredAndGroupedChecks = useGroupedByPriorityTagChecks(sortedAndFilteredChecks);

  const noResultsFound = useMemo(() => {
    return Object.values(sortedFilteredAndGroupedChecks).every((group) => group.count === 0);
  }, [sortedFilteredAndGroupedChecks]);

  return (
    <>
      <SwipeablePageWrapper
        refetch={refetchChecks}
        isFull
        justifyContent="flex-start"
        flexDirection="column"
        h="100%"
        isLoading={isLoadingChecks}
      >
        <NavigationHeader
          title={intl.formatMessage({
            id: "pages.inventory.rolling-inventory-checks.title",
          })}
          onClickGoBack={() => {
            reset();
            navigate(routes.inventory.root);
          }}
        />
        <CheckScanner
          checksList={rawChecks}
          startCheck={startCheck}
          type={[TaskType.stock_check]}
          metadataReason={ROLLING_INVENTORY_METADATA_REASONS}
        />
        <Flex
          justify="space-between"
          w="100%"
          gap="s100"
          px="s200"
          pb="s200"
          pt={isAutoScanActive ? 0 : "s200"}
        >
          <DropDown
            options={shelfOptions}
            value={selectedShelf}
            onInput={setSelectedShelf}
            filterMode={false}
            w="150px"
            h="40px"
            placement="top"
            listStyles={{ w: "150px" }}
            popoverStyles={{ mr: "0" }}
          />
          <IconButton
            size="md"
            variant="subtle"
            bg="white"
            aria-label="Toggle shelf sort order"
            icon={<AscDscIcon height="24px" width="24px" sortState={shelfSortOrder} />}
            onClick={toggleShelfSortOrder}
          />
        </Flex>
        {noResultsFound ? (
          <Flex justify="center" align="center" h="100%" p="s200">
            <TitleM>
              {intl.formatMessage({
                id: "generic.warnings.no-results-found",
              })}
            </TitleM>
          </Flex>
        ) : (
          <Flex h="100%" direction="column" w="100%" overflowY="scroll">
            {Object.entries(sortedFilteredAndGroupedChecks).map(
              ([priorityTag, { checks, count }]) => {
                if (count === 0) {
                  return null;
                }
                return (
                  <Fragment key={priorityTag}>
                    <Flex w="100%" alignItems="center" px="s200" pt="s100" mb="s100">
                      <TitleM>
                        {intl.formatMessage({
                          id: `flows.inventory.stock-checks.pages.stock-checks-list.prioriry-group-${priorityTag}` as IntlMessageId,
                        })}
                      </TitleM>
                      <Circle minW="20px" minH="20px" px="s50" bg="grey.600" m="s100">
                        <TitleXS color="white">{count}</TitleXS>
                      </Circle>
                    </Flex>
                    <ChecksList items={checks} onSelectCheck={onSelectCheck} />
                  </Fragment>
                );
              },
            )}
          </Flex>
        )}
      </SwipeablePageWrapper>
      <TaskIsTakenModal
        isOpen={isTaskIsTakenModalOpen}
        onClose={onCloseTaskIsTakenModal}
        onRefresh={refetchChecks}
      />
      <ConnectionErrorModal
        isOpen={isErrorModalOpen}
        onClose={onCloseErrorModal}
        onRefresh={refetchChecks}
      />
      <SpinnerModal isOpen={isLoadingFetchNextCheckAndStartCheck} />
    </>
  );
}
